import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AccessibilityProvider } from './context/AccessibilityContext';
import { Toaster } from './components/ui/sonner';
import AuthCallback from './pages/AuthCallback';
import Landing from './pages/Landing';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import AppLayout from './components/AppLayout';
import TodayPage from './pages/TodayPage';
import TasksPage from './pages/TasksPage';
import ProjectsPage from './pages/ProjectsPage';
import ChatsPage from './pages/ChatsPage';
import AgentsPage from './pages/AgentsPage';
import DiscoverPage from './pages/DiscoverPage';
import MemoryPage from './pages/MemoryPage';
import SkillsPage from './pages/SkillsPage';
import SettingsPage from './pages/SettingsPage';
import UpgradePage from './pages/UpgradePage';
import HelpPage from './pages/HelpPage';
import PublicHelp from './pages/PublicHelp';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Security from './pages/Security';
import TermsOfService from './pages/TermsOfService';
import { PaymentSuccess, PaymentCancel } from './pages/PaymentResult';
import './App.css';

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="h-dvh flex items-center justify-center bg-[hsl(var(--background))]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-[hsl(var(--primary))] animate-pulse" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading your workspace…</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/help" element={<PublicHelp />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/security" element={<Security />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/app" element={<Protected><AppLayout /></Protected>}>
        <Route index element={<Navigate to="/app/today" replace />} />
        <Route path="today" element={<TodayPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="chats" element={<ChatsPage />} />
        <Route path="chats/:chatId" element={<ChatsPage />} />
        <Route path="agents" element={<AgentsPage />} />
        <Route path="discover" element={<DiscoverPage />} />
        <Route path="memory" element={<MemoryPage />} />
        <Route path="skills" element={<SkillsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="upgrade" element={<UpgradePage />} />
      </Route>
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AccessibilityProvider>
            <AppRouter />
            <Toaster position="top-center" richColors />
          </AccessibilityProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
